import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import {
  Coins, CreditCard, Upload, Copy, CheckCircle2, ArrowLeft, Loader2,
  Clock, XCircle, Wallet, ShoppingCart, Smartphone, Receipt,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';

const STATUS_LABELS = {
  pending: { label: 'En revisión', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
  approved: { label: 'Aprobado', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle2 },
  rejected: { label: 'Rechazado', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
};

const TX_LABELS = {
  spend: { label: 'Gasto', color: 'text-red-400' },
  add: { label: 'Recarga', color: 'text-green-400' },
};

export default function CreditsPage() {
  const { token, user } = useAuth();
  const [balance, setBalance] = useState(0);
  const [meta, setMeta] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ payment_method: 'zelle', reference: '', proof_image: '', note: '' });

  const headers = () => ({ Authorization: `Bearer ${token}` });

  const load = async () => {
    setLoading(true);
    try {
      const [bal, ord] = await Promise.all([
        axios.get(`${API}/credits/balance`, { headers: headers() }),
        axios.get(`${API}/credits/my-orders`, { headers: headers() }),
      ]);
      setBalance(bal.data.balance);
      setMeta({
        cost: bal.data.cost_per_feature,
        package: bal.data.topup_package,
        zelle: bal.data.zelle_phone,
        methods: bal.data.payment_methods || {},
      });
      setTransactions(bal.data.recent_transactions || []);
      setOrders(ord.data.orders || []);
    } catch (e) {
      toast.error('Error cargando datos de créditos');
    }
    setLoading(false);
  };

  useEffect(() => { if (token) load(); }, [token]);

  const copyDestination = () => {
    navigator.clipboard.writeText(meta.methods?.[form.payment_method]?.destination || '');
    toast.success('Destino copiado');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Imagen demasiado grande (max 3MB)');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setForm(f => ({ ...f, proof_image: reader.result }));
    reader.readAsDataURL(file);
  };

  const submitPurchase = async () => {
    if (!form.reference.trim() || form.reference.trim().length < 2) {
      toast.error('Ingresa la referencia del pago');
      return;
    }
    if (!meta.methods?.[form.payment_method]) {
      toast.error('Elige un método de pago configurado');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/credits/purchase-request`,
        { payment_method: form.payment_method, reference: form.reference.trim(), proof_image: form.proof_image || null, note: form.note || null },
        { headers: headers() }
      );
      toast.success(res.data.message);
      setPurchaseOpen(false);
      setForm({ payment_method: '', reference: '', proof_image: '', note: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar solicitud');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center pt-32">
          <Loader2 className="animate-spin text-amber-400" size={32} />
        </div>
      </div>
    );
  }

  const pkg = meta.package || { credits: 50, price_usd: 20 };
  const numUses = Math.floor(balance / (meta.cost || 10));

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 py-8 pt-24" data-testid="credits-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard" className="text-slate-500 hover:text-slate-300">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
                <Coins size={22} className="text-white" />
              </div>
              <h1 className="text-3xl font-black text-white" data-testid="credits-title">Mis Créditos</h1>
            </div>
            <p className="text-slate-500 text-sm mt-1 ml-14">Cada función predictiva cuesta {meta.cost || 10} créditos</p>
          </div>
        </div>

        {/* Balance Card */}
        <Card className="bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-red-500/10 border-amber-500/30 mb-6" data-testid="balance-card">
          <CardContent className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-xl shadow-amber-500/30">
                <Wallet size={28} className="text-white" />
              </div>
              <div>
                <p className="text-amber-300 text-xs uppercase font-bold tracking-wider">Balance Actual</p>
                <p className="text-5xl font-black text-white" data-testid="credits-balance">{balance}</p>
                <p className="text-slate-400 text-xs mt-1">Te alcanza para <strong className="text-amber-300">{numUses}</strong> generaciones</p>
              </div>
            </div>
            <Button
              onClick={() => setPurchaseOpen(true)}
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold shadow-lg shadow-amber-500/20"
              data-testid="open-purchase-btn"
            >
              <ShoppingCart size={18} className="mr-2" />
              Comprar {pkg.credits} créditos · ${pkg.price_usd}
            </Button>
          </CardContent>
        </Card>

        {/* How it works */}
        <Card className="bg-slate-900/60 border-slate-800 mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Receipt size={18} className="text-amber-400" />
              ¿Cómo funciona?
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-slate-400">
            <div className="p-3 rounded-lg bg-slate-950/60 border border-slate-800">
              <div className="text-amber-400 font-bold mb-1">1️⃣ Membresía</div>
              <p>Al pagar la membresía mensual de $19.99 recibes <strong className="text-white">50 créditos gratis</strong>.</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-950/60 border border-slate-800">
              <div className="text-amber-400 font-bold mb-1">2️⃣ Usa funciones</div>
              <p>Cada generación (Sócrates, Tauro, 7IA, Aries, Jugada, etc.) cuesta <strong className="text-white">{meta.cost || 10} créditos</strong>.</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-950/60 border border-slate-800">
              <div className="text-amber-400 font-bold mb-1">3️⃣ Recarga</div>
              <p>Cuando se agoten, compra <strong className="text-white">{pkg.credits} créditos por ${pkg.price_usd}</strong> mediante un método disponible.</p>
            </div>
          </CardContent>
        </Card>

        {/* Two columns: orders + transactions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Orders */}
          <Card className="bg-slate-900/60 border-slate-800" data-testid="orders-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <CreditCard size={18} className="text-orange-400" />
                Mis Órdenes de Recarga
              </CardTitle>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-6">No tienes órdenes aún.</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {orders.map(o => {
                    const st = STATUS_LABELS[o.status] || STATUS_LABELS.pending;
                    const Icon = st.icon;
                    return (
                      <div key={o.id} className="p-3 rounded-lg bg-slate-950/60 border border-slate-800" data-testid={`order-${o.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono text-amber-400 text-xs">{o.reference}</span>
                          <Badge className={st.color}>
                            <Icon size={10} className="mr-1" /> {st.label}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between gap-3 text-xs">
                          <span className="text-slate-400">+{o.credits_to_add} créditos · ${o.amount_usd} USD</span>
                          <span className="text-slate-600">{new Date(o.created_at).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        {o.admin_note && (
                          <p className="mt-1 text-[10px] text-slate-500 italic">Nota admin: {o.admin_note}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Transactions */}
          <Card className="bg-slate-900/60 border-slate-800" data-testid="transactions-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Receipt size={18} className="text-cyan-400" />
                Movimientos Recientes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-6">Sin movimientos.</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {transactions.map((tx, i) => {
                    const t = TX_LABELS[tx.type] || { label: tx.type, color: 'text-slate-400' };
                    return (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-950/40 text-sm">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-bold ${t.color}`}>{tx.amount > 0 ? `+${tx.amount}` : tx.amount}</span>
                            <span className="text-slate-300 text-xs truncate">
                              {tx.feature ? `Uso: ${tx.feature}` : (tx.source === 'subscription_initial' ? 'Bono de membresía' : tx.source === 'zelle_topup' ? `Recarga` : tx.source || 'Recarga')}
                            </span>
                          </div>
                          <div className="text-[10px] text-slate-600">{new Date(tx.created_at).toLocaleString('es-DO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                        <span className="text-slate-500 text-xs">Saldo: {tx.balance_after}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Purchase Dialog */}
        <Dialog open={purchaseOpen} onOpenChange={setPurchaseOpen}>
          <DialogContent className="bg-slate-950 border-slate-800 text-white max-w-lg" data-testid="purchase-dialog">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <ShoppingCart size={20} className="text-amber-400" />
                Comprar {pkg.credits} créditos por ${pkg.price_usd}
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Envía ${pkg.price_usd} por el método elegido y registra tu referencia. Un administrador verificará el cobro.
              </DialogDescription>
            </DialogHeader>

            <div className="flex gap-2">
              {Object.entries(meta.methods || {}).map(([key, info]) => (
                <Button key={key} variant={form.payment_method === key ? 'default' : 'outline'}
                  data-testid={`payment-method-${key}`}
                  onClick={() => setForm({ ...form, payment_method: key })}>{info.label}</Button>
              ))}
            </div>
            {Object.keys(meta.methods || {}).length === 0 && <p>No hay métodos de cobro configurados.</p>}
            {/* Payment destination */}
            {meta.methods?.[form.payment_method] &&
            <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30">
              <div className="flex items-center gap-2 mb-3">
                <Smartphone size={18} className="text-purple-400" />
                <span className="text-purple-300 font-bold text-sm uppercase tracking-wider">Enviar por {meta.methods[form.payment_method].label}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-950/60 border border-slate-800">
                <div>
                  <p className="text-slate-500 text-[10px] uppercase">Destino de pago</p>
                  <p className="font-mono font-black text-white text-base break-all" data-testid="payment-destination">{meta.methods[form.payment_method].destination}</p>
                </div>
                <Button size="sm" variant="outline" onClick={copyDestination} className="border-purple-500/30 text-purple-300 hover:bg-purple-500/10" data-testid="copy-destination-btn">
                  <Copy size={14} className="mr-1" /> Copiar
                </Button>
              </div>
              <p className="text-amber-300 text-xs mt-2">Monto a enviar: <strong>${pkg.price_usd} USD</strong></p>
              {form.payment_method === 'paypal' && <a href={meta.methods.paypal.destination} target="_blank" rel="noopener noreferrer" className="text-sky-300 underline text-sm">Abrir enlace PayPal</a>}
              {form.payment_method === 'cash_app' && <a href={meta.methods.cash_app.url} target="_blank" rel="noopener noreferrer" className="text-sky-300 underline text-sm">Abrir perfil Cash App</a>}
            </div>}

            {/* Form */}
            <div className="space-y-3">
              <div>
                <label className="text-sm text-slate-300 mb-1.5 block">Referencia o ID de transacción <span className="text-red-400">*</span></label>
                <Input
                  value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  placeholder="Referencia del método elegido"
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="reference-input"
                />
              </div>

              <div>
                <label className="text-sm text-slate-300 mb-1.5 block">Comprobante del pago</label>
                <div className="flex items-center gap-2">
                  <label className="flex-1 flex items-center gap-2 p-2 rounded-lg border border-dashed border-slate-700 bg-slate-900/50 cursor-pointer hover:border-amber-500/50 transition-colors">
                    <Upload size={16} className="text-slate-500" />
                    <span className="text-slate-400 text-xs truncate">
                      {form.proof_image ? 'Imagen cargada ✓' : 'Subir comprobante (opcional, max 3MB)'}
                    </span>
                    <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" data-testid="proof-upload" />
                  </label>
                </div>
                {form.proof_image && (
                  <img src={form.proof_image} alt="proof" className="mt-2 max-h-32 rounded-lg border border-slate-700" />
                )}
              </div>

              <div>
                <label className="text-sm text-slate-300 mb-1.5 block">Nota (opcional)</label>
                <Textarea
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Algún detalle que el admin deba saber..."
                  className="bg-slate-900 border-slate-700 text-white text-sm"
                  rows={2}
                  data-testid="note-input"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setPurchaseOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                Cancelar
              </Button>
              <Button
                onClick={submitPurchase}
                disabled={submitting || !form.payment_method || !form.reference.trim()}
                className="bg-gradient-to-r from-amber-500 to-orange-600 text-white"
                data-testid="submit-purchase-btn"
              >
                {submitting ? <Loader2 size={16} className="animate-spin mr-1" /> : <CheckCircle2 size={16} className="mr-1" />}
                Enviar solicitud
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
